import ScreenshotViewer, { screenshotOffsetMs } from './ScreenshotViewer'
import { useState, useRef, useEffect, useCallback } from 'react'
import type { CSSProperties } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import type { Bug, EvidenceItem, AnalysisSection, ChronologyStep, JudgeResult, JudgeSubmission, Project } from '../data/projectTypes'
import { stepScreenshotUrl } from '../data/projectTypes'
import { Link } from './nav'
import StatusBadge from './StatusBadge'
import BugEnvironment from './BugEnvironment'
import { ArrowLeft, ChevronDown, ChevronRight, Film, Trash2, Copy, Download, Check, CheckCircle2, AlertTriangle, XCircle, FileCode, Terminal, Image as ImageIcon, Globe, MousePointerClick, GitCommitHorizontal, Search, Scan, Braces, SquareTerminal, Flag, Database } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import Spinner from './Spinner'
import TaskEventReplay from './TaskEventReplay'
import type { ReplaySource } from './TaskEventReplay'
import BugJudgeLinks from './BugJudgeLinks'
import { SEVERITIES, SEVERITY_TOOLTIPS } from './bugSeverity'
import { USER_SETTABLE_STATUSES, STATUS_LABELS, STATUS_TOOLTIPS } from '../../netlify/functions/lib/bug-statuses'
import { bugCategoryLabel } from '../../netlify/functions/lib/polish-pass-types'
import { downloadTextFile, sanitizeFilename } from '../lib/downloadFile'
import { useAdminMode } from '../hooks/useAdminMode'
import { useChangeBugSeverityMutation, useChangeBugStatusMutation, useResolveEvidenceMutation, useUpdateBugDetailsMutation } from '../services/projectDataApi'
import './OverviewExplorations.css'
import { securityImpact } from '../lib/securityImpact'
import { isIssueTrackerConnected } from '../lib/issueTracker'
import ConnectIssueTrackerButton from './ConnectIssueTrackerButton'
import BugPullRequests from './BugPullRequests'
import './BugReport.css'

// One segment of a bug's video_spec — the judge's curated walkthrough range + subtitle, in ms
// offsets from the recording start.
interface WalkthroughSegment {
  recording_id?: string
  context?: string
  start_time: number
  end_time: number
  subtitle: string
}

// Derive an rrweb-clip walkthrough from a bug's video_spec: the overall [start, end] window across
// segments, plus the curated subtitle track. Recording selection is handled separately because a
// bug can carry an exact replay_recording_id even when it has no test_run_id (for example, polish).
function buildWalkthrough(bug: Bug): { clip: { start: number; end: number }; subtitleSegments: { start: number; end: number; text: string }[] } | null {
  const raw = (bug.video_spec as { segments?: unknown } | null)?.segments
  if (!Array.isArray(raw)) return null
  const segments = raw.filter(
    (s): s is WalkthroughSegment =>
      !!s && typeof (s as WalkthroughSegment).start_time === 'number' && typeof (s as WalkthroughSegment).end_time === 'number',
  )
  if (segments.length === 0) return null
  const start = Math.min(...segments.map(s => s.start_time))
  const end = Math.max(...segments.map(s => s.end_time))
  if (!(end > start)) return null
  const subtitleSegments = segments.map(s => ({
    start: s.start_time,
    end: s.end_time,
    text: s.context ? `${s.context}: ${s.subtitle}` : s.subtitle,
  }))
  return { clip: { start, end }, subtitleSegments }
}


interface BugReportProps {
  bug: Bug
  backLabel?: string
  onNavigateBack: () => void
  onDeleteBug: () => void
  mobileSidebar?: React.ReactNode
  // When the page renders the metadata rail (BugDetail), status is changed there and the toolbar
  // drops the "Delete Bug" action; embedded uses without a rail keep the delete button.
  showStatusSelector?: boolean
}

type EditableField = 'title' | 'description' | 'reproduction_steps' | 'expected_behavior' | 'actual_behavior'

const FIELD_LABELS: Record<EditableField, string> = {
  title: 'Title',
  description: 'Description',
  reproduction_steps: 'Steps to Reproduce',
  expected_behavior: 'Expected Behavior',
  actual_behavior: 'Actual Behavior',
}

// Prefer identifiers that point at the exact recording which produced the report. A test run can
// have multiple attempts or contexts, so test_run_id alone may resolve to a newer/different stream.
// Older reports fall back through the recording id embedded in video_spec, the run's recording map,
// and finally the broader run/task identifiers.
function replaySourceForBug(bug: Bug): ReplaySource | null {
  if (bug.replay_recording_id) return { recordingId: bug.replay_recording_id }

  const rawSegments = (bug.video_spec as { segments?: unknown } | null)?.segments
  if (Array.isArray(rawSegments)) {
    const segmentRecordingId = rawSegments.find(
      (segment): segment is WalkthroughSegment =>
        !!segment && typeof segment === 'object' && typeof (segment as WalkthroughSegment).recording_id === 'string',
    )?.recording_id
    if (segmentRecordingId) return { recordingId: segmentRecordingId }
  }

  const runRecordingId = Object.values(bug.test_run_replay_recording_ids ?? {}).find(Boolean)
  if (runRecordingId) return { recordingId: runRecordingId }
  if (bug.test_run_id) return { testRunId: bug.test_run_id }
  if (bug.task_id) return { taskId: bug.task_id }
  return null
}

// The bug's evidence, in the same in-flow card the project overview uses for its exploration replay
// (`ov-explore`, OverviewExplorations.css) rather than the expand-to-play cover this used to be: it
// sits under the report header and plays where it is, so nothing about the page moves to watch it.
//
// Curated video specs define the preferred clip. When only a replay screenshot exists, its timestamp
// anchors a short clip around the captured frame so the screenshot still leads to the surrounding
// behavior.
function BugReplayCard({ bug }: { bug: Bug }) {
  const walkthrough = buildWalkthrough(bug)
  const screenshotUrl = bug.screenshot_spec?.screenshot_url ?? null
  const screenshotMs = screenshotOffsetMs(screenshotUrl)
  const source = replaySourceForBug(bug)
  const screenshotClip = screenshotMs == null
    ? null
    : { start: Math.max(0, screenshotMs - 4_000), end: screenshotMs + 4_000 }
  const clip = walkthrough?.clip ?? screenshotClip
  const subtitleSegments = walkthrough?.subtitleSegments
    ?? (screenshotClip ? [{ ...screenshotClip, text: bug.title }] : null)
  // The recording itself is enough to render the card. video_spec and screenshots only narrow the
  // clip; requiring either hid valid recording-backed bug reports that had neither enrichment.
  const hasReplay = !!source
  if (!hasReplay && !bug.video_url && !screenshotUrl) return null

  return (
    <section className="ov-explore bug-replay" data-testid="bug-replay">
      <div className="ov-explore__head">
        <span className="ov-explore__title">
          <Film size={18} strokeWidth={1.75} aria-hidden /> {hasReplay || bug.video_url ? 'Replay' : 'Screenshot'}
        </span>
      </div>
      <div className="ov-explore__stage">
        {hasReplay && source ? (
          <TaskEventReplay source={source} active live={false} clip={clip} subtitleSegments={subtitleSegments} />
        ) : bug.video_url ? (
          <video
            src={bug.video_url}
            poster={screenshotUrl ?? undefined}
            controls
            loop
            muted
            playsInline
            className="bug-replay__media"
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={screenshotUrl!}
            alt="Screenshot highlighting the bug"
            className="bug-replay__media"
          />
        )}
      </div>
    </section>
  )
}

// Evidence rows name the tool that produced them ("ReadSource", "code_search"). Readers want a plain
// label and a glyph for the kind of evidence, plus a one-line hint of what was looked at; the raw
// params / result stay behind the disclosure.
const EVIDENCE_TOOLS: Record<string, { label: string; icon: LucideIcon }> = {
  readsource: { label: 'Source', icon: FileCode },
  code_search: { label: 'Code search', icon: Search },
  logpoint: { label: 'Logpoint', icon: Terminal },
  evaluate: { label: 'Evaluate', icon: Braces },
  screenshot: { label: 'Screenshot', icon: ImageIcon },
  describescreenshot: { label: 'Screenshot', icon: ImageIcon },
  consolemessages: { label: 'Console', icon: SquareTerminal },
  networkrequest: { label: 'Network', icon: Globe },
  network_replay: { label: 'Network', icon: Globe },
  userinteractions: { label: 'User interactions', icon: MousePointerClick },
  inspectelement: { label: 'Inspect element', icon: Scan },
  git_blame: { label: 'Git blame', icon: GitCommitHorizontal },
  feature_flag_audit: { label: 'Feature flags', icon: Flag },
  schema_diff: { label: 'Schema diff', icon: Database },
  provideddata: { label: 'Provided data', icon: Database },
}

function evidenceTool(tool: unknown): { label: string; icon: LucideIcon } {
  const normalizedTool = typeof tool === 'string' ? tool.trim() : ''
  if (!normalizedTool) return { label: 'Evidence', icon: Search }
  const known = EVIDENCE_TOOLS[normalizedTool.toLowerCase()]
  if (known) return known
  // "ReactPerformanceInsights" / "trace_span" → "React performance insights" / "Trace span".
  const words = normalizedTool.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase()
  return { label: words.charAt(0).toUpperCase() + words.slice(1), icon: Search }
}

// The most descriptive scalar in the params — a file, URL, expression or query — for the row preview.
const EVIDENCE_PREVIEW_KEYS = ['url', 'path', 'file', 'filename', 'source', 'query', 'pattern', 'expression', 'selector', 'text', 'message', 'name']

function evidencePreview(params: Record<string, unknown>): string | null {
  const entries = Object.entries(params ?? {}).filter(([k, v]) => k !== 'recordingId' && k !== 'recording_id' && (typeof v === 'string' || typeof v === 'number'))
  if (entries.length === 0) return null
  const preferred = EVIDENCE_PREVIEW_KEYS.map(k => entries.find(([key]) => key.toLowerCase() === k)).find(Boolean)
  const [, value] = preferred ?? entries[0]!
  const text = String(value).replace(/\s+/g, ' ').trim()
  return text.length > 90 ? `${text.slice(0, 89)}…` : text
}

export function EvidencePanel({ items, recordingId }: { items: EvidenceItem[]; recordingId: string | null }) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  if (!items || items.length === 0) return null

  return (
    <div className="evidence-panel">
      <div className="evidence-panel__label">
        Evidence
        <span className="evidence-panel__count">{items.length}</span>
      </div>
      <div className="evidence-panel__list">
        {items.map((item, idx) => {
          const isExpanded = expandedIndex === idx
          const hasResult = item.result != null
          const paramsDisplay = { ...item.params }
          if (recordingId) paramsDisplay.recordingId = recordingId
          const { label, icon: Icon } = evidenceTool(item.tool)
          const toolName = typeof item.tool === 'string' && item.tool.trim() ? item.tool : 'Evidence'
          const preview = evidencePreview(item.params)

          return (
            <div key={idx} className={`evidence-item${isExpanded ? ' evidence-item--open' : ''}`} data-testid={`evidence-item-${idx}`}>
              <button
                type="button"
                className="evidence-item__header"
                onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                aria-expanded={isExpanded}
                data-analytics-event="bug-evidence-item-toggle"
              >
                <Icon size={14} className="evidence-item__icon" aria-hidden="true" />
                <span className="evidence-item__tool" title={toolName}>{label}</span>
                {preview && <span className="evidence-item__preview">{preview}</span>}
                {isExpanded
                  ? <ChevronDown size={14} className="evidence-item__chevron" aria-hidden="true" />
                  : <ChevronRight size={14} className="evidence-item__chevron" aria-hidden="true" />}
              </button>
              {isExpanded && (
                <div className="evidence-item__body">
                  <div className="evidence-item__params">
                    <span className="evidence-item__params-label">{toolName} · params</span>
                    <code>{JSON.stringify(paramsDisplay, null, 2)}</code>
                  </div>
                  {hasResult && (
                    <div className="evidence-item__result">
                      <span className="evidence-item__result-label">Result</span>
                      <pre>{item.result}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function AnalysisSectionView({ section, label, recordingId }: { section: AnalysisSection; label: string; recordingId: string | null }) {
  return (
    <section className="bug-report__section" data-testid={`analysis-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <h2>{label}</h2>
      <p>{section.text}</p>
      {section.evidence && section.evidence.length > 0 && (
        <EvidencePanel items={section.evidence} recordingId={recordingId} />
      )}
    </section>
  )
}

// Convert a chronology step's normalized highlight box ([x, y, w, h] fractions of the frame, 0–1) into an
// overlay style (percentages) that tracks the image at any rendered size. Mirrors the hero screenshot box —
// clamps defensively. Returns null when there is no valid 4-tuple box.
function chronologyBoxStyle(box: number[] | null | undefined): CSSProperties | null {
  if (!Array.isArray(box) || box.length < 4) return null
  const clamp = (n: number) => Math.max(0, Math.min(1, typeof n === 'number' ? n : 0))
  const [x, y, w, h] = box as [number, number, number, number]
  return {
    left: `${clamp(x) * 100}%`,
    top: `${clamp(y) * 100}%`,
    width: `${clamp(w) * 100}%`,
    height: `${clamp(h) * 100}%`,
  }
}

// Position the red interaction circle at a normalized point ([x, y] fractions of the frame, 0–1).
// The element is centered on the point via translate(-50%, -50%), so it tracks the image at any size.
// Returns null when there is no valid point.
function chronologyCircleStyle(loc: { x: number; y: number } | null | undefined): CSSProperties | null {
  if (!loc || typeof loc.x !== 'number' || typeof loc.y !== 'number') return null
  const clamp = (n: number) => Math.max(0, Math.min(1, n))
  return {
    left: `${clamp(loc.x) * 100}%`,
    top: `${clamp(loc.y) * 100}%`,
  }
}

// Compact human-readable summary of a step's interaction, e.g. "🖱 click on 'Save'" or "⌨ typed Enter".
function interactionCaption(info: ChronologyStep['interaction_info']): string | null {
  if (!info) return null
  const parts: string[] = []
  parts.push(info.type === 'keyboard' ? '⌨' : '🖱')
  if (info.action) parts.push(info.action)
  if (info.keys) parts.push(`"${info.keys}"`)
  const label = info.target?.label || info.target?.selector
  if (label) parts.push(`on ${label}`)
  return parts.join(' ')
}

export function ChronologySection({ steps, recordingId, stepLabels }: { steps: ChronologyStep[]; recordingId: string | null; stepLabels?: React.ReactNode[] }) {
  if (!steps || steps.length === 0) return null

  return (
    <section className="bug-report__section" data-testid="analysis-chronology">
      <h2>Chronology of Events</h2>
      <ol className="bug-report__chronology-list">
        {steps.map((step, idx) => {
          const boxStyle = chronologyBoxStyle(step.highlight_box)
          const circleStyle = chronologyCircleStyle(step.interaction_info?.location)
          const caption = interactionCaption(step.interaction_info)
          // The frame comes from the step's Screenshot evidence, so it is the same image the evidence
          // panel below reports — the two cannot disagree.
          const frame = stepScreenshotUrl(step)
          const alt = `Screenshot for step ${idx + 1}: ${(step.text ?? '').slice(0, 80)}`
          return (
          <li key={idx} className="bug-report__chronology-step" data-testid={`chronology-step-${idx}`}>
            {stepLabels?.[idx] && <div className="bug-report__chronology-step-label">{stepLabels[idx]}</div>}
            {frame && (
              <div className="bug-report__chronology-content">
                <ScreenshotViewer
                  frame={frame}
                  alt={alt}
                  boxStyle={boxStyle}
                  circleStyle={circleStyle}
                  testIdPrefix={`chronology-step-${idx}`}
                />
                <div className="bug-report__chronology-copy">
                  <p className="bug-report__chronology-text">{step.text ?? ''}</p>
                  {caption && (
                    <p className="bug-report__chronology-interaction" data-testid={`chronology-step-${idx}-interaction`}>
                      {caption}
                    </p>
                  )}
                </div>
              </div>
            )}
            {!frame && (
              <div className="bug-report__chronology-copy">
                <p className="bug-report__chronology-text">{step.text ?? ''}</p>
                {caption && (
                  <p className="bug-report__chronology-interaction" data-testid={`chronology-step-${idx}-interaction`}>
                    {caption}
                  </p>
                )}
              </div>
            )}
            {step.evidence && step.evidence.length > 0 && (
              <EvidencePanel items={step.evidence} recordingId={recordingId} />
            )}
          </li>
          )
        })}
      </ol>
    </section>
  )
}

function JudgePhaseRow({ label, result }: { label: string; result: JudgeResult }) {
  return (
    <div className="bug-report__judge-phase">
      <div className="bug-report__judge-phase-header">
        <span className={`bug-report__judge-phase-badge ${result.approved ? 'bug-report__judge-phase-badge--passed' : 'bug-report__judge-phase-badge--failed'}`}>
          {label}: {result.approved ? 'Passed' : 'Failed'}
        </span>
      </div>
      {result.problems?.length > 0 && (
        <ul className="bug-report__judge-problems">
          {result.problems.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
      {result.assessment && <p className="bug-report__judge-assessment-text">{result.assessment}</p>}
    </div>
  )
}

// One submission's judge review inside the expanded unconfirmed warning: attempt number, verdict
// badge and timestamp, then the per-phase verdicts when available, otherwise the combined
// assessment and problems.
function JudgeSubmissionReview({ sub }: { sub: JudgeSubmission }) {
  const result = sub.result ?? { approved: false, problems: [] as string[] }
  const hasPhases = !!sub.evidence_result || !!sub.requirements_result
  return (
    <div className={`bug-report__judge-attempt ${result.approved ? 'bug-report__judge-attempt--approved' : 'bug-report__judge-attempt--rejected'}`}>
      <div className="bug-report__judge-attempt-header">
        <span className="bug-report__judge-attempt-number">Attempt {sub.attempt}</span>
        <span className={`bug-report__judge-attempt-badge ${result.approved ? 'bug-report__judge-attempt-badge--approved' : 'bug-report__judge-attempt-badge--rejected'}`}>
          {result.approved ? 'Approved' : 'Rejected'}
        </span>
        <time className="bug-report__judge-attempt-time">{new Date(sub.submitted_at).toLocaleString()}</time>
      </div>
      {hasPhases ? (
        <>
          {sub.evidence_result && <JudgePhaseRow label="Evidence" result={sub.evidence_result} />}
          {sub.requirements_result && <JudgePhaseRow label="Requirements" result={sub.requirements_result} />}
        </>
      ) : (
        <>
          {result.problems?.length > 0 && (
            <ul className="bug-report__judge-problems">
              {result.problems.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}
          {result.assessment && <p className="bug-report__judge-assessment-text">{result.assessment}</p>}
        </>
      )}
    </div>
  )
}

// A discarded report can retain its judge reviews for context. Show those reviews newest first,
// without implying that discarding a report fixed the underlying behavior.
function DiscardedReportNotice({ bug }: { bug: Bug }) {
  const [expanded, setExpanded] = useState(false)
  const submissions: JudgeSubmission[] = bug.judge_submissions ?? []
  const fallback: JudgeResult | null = bug.judge_assessment ?? null
  if (submissions.length === 0 && !fallback) return null

  // Newest submission first.
  const ordered = [...submissions].reverse()

  return (
    <section className="bug-report__unconfirmed" data-testid="bug-unconfirmed-warning">
      <button
        type="button"
        className="bug-report__unconfirmed-banner"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        data-testid="bug-unconfirmed-toggle"
        data-analytics-event="bug-unconfirmed-toggle"
      >
        <AlertTriangle size={18} className="bug-report__unconfirmed-icon" aria-hidden="true" />
        <span className="bug-report__unconfirmed-text">
          This report was discarded as invalid.
        </span>
        <span className="bug-report__unconfirmed-expand">
          {expanded ? 'Hide judge reviews' : 'Show judge reviews'}
          {expanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        </span>
      </button>

      {expanded && (
        <div className="bug-report__unconfirmed-review" data-testid="bug-unconfirmed-review">
          {ordered.length > 0 ? (
            <>
              <div className="bug-report__judge-history-label">
                {ordered.length} submission{ordered.length > 1 ? 's' : ''}
              </div>
              {ordered.map((sub) => <JudgeSubmissionReview key={sub.attempt} sub={sub} />)}
            </>
          ) : fallback && (
            <div className="bug-report__judge-verdict bug-report__judge-verdict--rejected">
              <XCircle size={18} className="bug-report__judge-verdict-icon" aria-hidden="true" />
              <div className="bug-report__judge-verdict-text">
                <strong>Judge review</strong>
                {fallback.assessment && <p className="bug-report__judge-assessment-text">{fallback.assessment}</p>}
                {fallback.problems?.length > 0 && (
                  <ul className="bug-report__judge-problems">
                    {fallback.problems.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

// [bug-authoring]
export function buildMarkdown(bug: Bug): string {
  const parts: string[] = []
  parts.push(`# ${bug.title}`)

  if (bug.description) {
    parts.push(`\n## Description\n${bug.description}`)
  }

  if (bug.analysis?.chronology?.length) {
    parts.push('\n## Steps to Reproduce')
    for (let i = 0; i < bug.analysis.chronology.length; i++) {
      const step = bug.analysis.chronology[i]!
      parts.push(`${i + 1}. ${step.text ?? ''}`)
      const frame = stepScreenshotUrl(step)
      if (frame) {
        parts.push(`   Screenshot: ${frame}`)
      }
    }
  } else if (bug.reproduction_steps) {
    parts.push(`\n## Steps to Reproduce\n${bug.reproduction_steps}`)
  }

  if (bug.expected_behavior) {
    parts.push(`\n## Expected Behavior\n${bug.expected_behavior}`)
  }

  if (bug.polish_category === 'security' && bug.analysis?.impact?.trim()) {
    parts.push(`\n## Impact\n${bug.analysis.impact.trim()}`)
  }

  if (bug.analysis?.root_cause?.text) {
    parts.push(`\n## Root Cause\n${bug.analysis.root_cause.text}`)
  }

  if (bug.analysis?.chain?.length) {
    parts.push('\n## Causal Chain')
    for (const step of bug.analysis.chain) {
      parts.push(`- ${step.text ?? ''}`)
    }
  }

  const recordings: string[] = []
  if (bug.replay_recording_id) {
    recordings.push(`https://app.replay.io/recording/${bug.replay_recording_id}`)
  }
  if (bug.test_run_replay_recording_ids) {
    for (const [context, id] of Object.entries(bug.test_run_replay_recording_ids)) {
      const url = `https://app.replay.io/recording/${id}`
      if (!recordings.includes(url)) {
        recordings.push(`${url} (${context})`)
      }
    }
  }
  if (recordings.length > 0) {
    parts.push(`\n## Replay Recordings\n${recordings.join('\n')}`)
  }

  if (bug.status === 'invalid') {
    const submissions = bug.judge_submissions ?? []
    const latest = submissions.length > 0 ? submissions[submissions.length - 1]!.result : bug.judge_assessment
    if (latest) {
      parts.push('\n## Discarded report')
      if (latest.assessment) parts.push(latest.assessment)
      if (latest.problems?.length) {
        if (latest.assessment) parts.push('')
        for (const p of latest.problems) parts.push(`- ${p}`)
      }
    }
  }

  return parts.join('\n')
}

// Copy / Download for the full markdown report. Rendered by the page at the top of the metadata
// rail (BugDetail), where GitHub and Graphite put a PR's primary actions.
export function BugReportActions({ bug, project }: { bug: Bug; project?: Project | null }) {
  const [copied, setCopied] = useState(false)

  function handleCopyMarkdown() {
    navigator.clipboard.writeText(buildMarkdown(bug)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // Save this bug's report as a markdown file named after the bug title, e.g. "Login-button-does-
  // nothing.md". Companion to the Copy button for users who'd rather keep a file than paste.
  function handleDownloadMarkdown() {
    const base = sanitizeFilename(bug.title, `bug-${bug.id}`)
    downloadTextFile(`${base}.md`, buildMarkdown(bug))
  }

  return (
    <div className="bug-report__header-actions" data-testid="bug-report-actions">
      <button
        type="button"
        className="bug-report__copy-btn bug-report__copy-btn--inline"
        onClick={handleCopyMarkdown}
        data-testid="copy-markdown-btn-inline"
        data-analytics-event="bug-report-copy-inline"
        aria-label={copied ? 'Copied' : 'Copy bug report'}
      >
        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
        {copied ? 'Copied' : 'Copy bug report'}
      </button>
      <button
        type="button"
        className="bug-report__download-btn bug-report__download-btn--inline"
        onClick={handleDownloadMarkdown}
        data-testid="download-markdown-btn-inline"
        data-analytics-event="bug-report-download-inline"
        aria-label="Download bug report"
      >
        <Download size={14} aria-hidden="true" />
        Download
      </button>
      {/* No issue tracker connected yet — offer the connect flow right beside the copy/download actions.
          Once one is connected this button disappears. */}
      {!isIssueTrackerConnected(project) && <ConnectIssueTrackerButton projectId={bug.project_id} />}
    </div>
  )
}

export default function BugReport({ bug, backLabel = 'Back to Project', onNavigateBack, onDeleteBug, mobileSidebar, showStatusSelector }: BugReportProps) {
  const user = useSelector((state: RootState) => state.auth.user)
  const [updateBugDetails] = useUpdateBugDetailsMutation()
  const [changeBugSeverity] = useChangeBugSeverityMutation()
  const [resolveEvidence] = useResolveEvidenceMutation()
  const adminMode = useAdminMode()
  const [editingField, setEditingField] = useState<EditableField | null>(null)
  const [editValue, setEditValue] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [showSeverityDropdown, setShowSeverityDropdown] = useState(false)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const statusRef = useRef<HTMLDivElement>(null)
  const statusBtnRef = useRef<HTMLButtonElement>(null)
  const [changeBugStatus] = useChangeBugStatusMutation()
  const [resolvingEvidence, setResolvingEvidence] = useState(false)
  const [markdownExpanded, setMarkdownExpanded] = useState(false)
  const severityRef = useRef<HTMLDivElement>(null)
  const severityBtnRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const cancellingRef = useRef(false)
  const savingRef = useRef(false)
  const evidenceResolvedRef = useRef(false)

  const displayBug = bug

  // The bug's category, shown in the header beside the severity. Same derivation as the bug
  // table's Kind column: a polish bug carries its pass type, a bug from a test run is `testing`,
  // and a manually entered bug has no category at all.
  const category = displayBug.polish_category ?? (displayBug.test_run_id ? 'testing' : null)

  const hasAnalysis = displayBug.analysis != null
  const hasUnresolvedEvidence = hasAnalysis && (() => {
    const a = displayBug.analysis!
    const items: EvidenceItem[] = [
      ...(a.chronology?.flatMap(s => s.evidence ?? []) ?? []),
      ...(a.root_cause?.evidence ?? []),
      ...(a.chain?.flatMap(s => s.evidence ?? []) ?? []),
    ]
    return items.some(e => e.result == null || (typeof e.result === 'string' && e.result.startsWith('MCP error')))
  })()

  useEffect(() => {
    if (user && hasUnresolvedEvidence && bug.replay_recording_id && !evidenceResolvedRef.current && !resolvingEvidence) {
      evidenceResolvedRef.current = true
      setResolvingEvidence(true)
      void resolveEvidence(bug.id)
        .finally(() => setResolvingEvidence(false))
    }
  }, [user, hasUnresolvedEvidence, bug.replay_recording_id, bug.id, resolveEvidence, resolvingEvidence])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (severityRef.current && !severityRef.current.contains(e.target as Node)) {
        setShowSeverityDropdown(false)
      }
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
        setShowStatusDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus()
    }
  }, [editingField])

  useEffect(() => {
    if (!editingField) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        cancellingRef.current = true
        setEditingField(null)
        setEditValue('')
        setValidationError(null)
        setTimeout(() => { cancellingRef.current = false }, 50)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [editingField])

  useEffect(() => {
    if (showSeverityDropdown && severityRef.current) {
      const firstItem = severityRef.current.querySelector('[role="menuitem"]') as HTMLElement
      firstItem?.focus()
    }
  }, [showSeverityDropdown])

  const startEdit = useCallback((field: EditableField) => {
    if (editingField === field) return
    setEditingField(field)
    setEditValue((bug[field] as string) ?? '')
    setValidationError(null)
  }, [editingField, bug])

  const saveField = useCallback((field: EditableField, value: string) => {
    if (cancellingRef.current) return
    if (savingRef.current) return
    if (field === 'title' && !value.trim()) {
      setValidationError('Title cannot be empty')
      return
    }
    savingRef.current = true
    void updateBugDetails({ id: bug.id, data: { [field]: value } as { title?: string; description?: string | null; reproduction_steps?: string | null; expected_behavior?: string | null; actual_behavior?: string | null } })
    setEditingField(null)
    setEditValue('')
    setValidationError(null)
    setTimeout(() => { savingRef.current = false }, 50)
  }, [bug.id, updateBugDetails])

  function handleBlur(field: EditableField) {
    saveField(field, editValue)
  }

  function handleKeyDownOnInput(e: React.KeyboardEvent, field: EditableField) {
    if (e.key === 'Enter' && field === 'title') {
      e.preventDefault()
      saveField(field, editValue)
    }
  }

  function handleStatusChange(newStatus: string) {
    void changeBugStatus({ id: bug.id, status: newStatus })
    setShowStatusDropdown(false)
    statusBtnRef.current?.focus()
  }

  // Arrow-key / Escape handling for the header's status chip menu (same behaviour as severity).
  function handleStatusDropdownKeyDown(e: React.KeyboardEvent) {
    const items = Array.from(statusRef.current?.querySelectorAll('[role="menuitem"]') ?? []) as HTMLElement[]
    const currentIndex = items.indexOf(e.target as HTMLElement)
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        items[(currentIndex + 1) % items.length]?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        items[(currentIndex - 1 + items.length) % items.length]?.focus()
        break
      case 'Escape':
        e.preventDefault()
        setShowStatusDropdown(false)
        statusBtnRef.current?.focus()
        break
    }
  }

  function handleSeverityChange(newSeverity: string) {
    void changeBugSeverity({ id: bug.id, severity: newSeverity })
    setShowSeverityDropdown(false)
    severityBtnRef.current?.focus()
  }

  function handleSeverityDropdownKeyDown(e: React.KeyboardEvent) {
    const items = Array.from(
      severityRef.current?.querySelectorAll('[role="menuitem"]') ?? []
    ) as HTMLElement[]
    const currentIndex = items.indexOf(e.target as HTMLElement)

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        items[(currentIndex + 1) % items.length]?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        items[(currentIndex - 1 + items.length) % items.length]?.focus()
        break
      case 'Escape':
        e.preventDefault()
        setShowSeverityDropdown(false)
        severityBtnRef.current?.focus()
        break
    }
  }

  function handleEditableKeyDown(e: React.KeyboardEvent, field: EditableField) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      startEdit(field)
    }
  }

  function renderEditableField(field: EditableField, label: string, multiline: boolean) {
    const value = (displayBug[field] as string) ?? ''
    const isEditing = editingField === field
    const errorId = `${field}-error`

    if (isEditing) {
      return multiline ? (
        <div>
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            className="bug-report__edit-textarea"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => handleBlur(field)}
            rows={4}
            aria-label={FIELD_LABELS[field]}
            aria-describedby={validationError && field === 'title' ? errorId : undefined}
            data-testid={`edit-${field}`}
            data-analytics-event={`bug-${field.replace(/_/g, '-')}-input`}
          />
          {validationError && field === 'title' && (
            <span className="bug-report__validation-error" id={errorId} role="alert" data-testid="validation-error">{validationError}</span>
          )}
        </div>
      ) : (
        <div>
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            className="bug-report__edit-input"
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => handleBlur(field)}
            onKeyDown={(e) => handleKeyDownOnInput(e, field)}
            aria-label={FIELD_LABELS[field]}
            aria-describedby={validationError && field === 'title' ? errorId : undefined}
            data-testid={`edit-${field}`}
            data-analytics-event={`bug-${field.replace(/_/g, '-')}-input`}
          />
          {validationError && field === 'title' && (
            <span className="bug-report__validation-error" id={errorId} role="alert" data-testid="validation-error">{validationError}</span>
          )}
        </div>
      )
    }

    const isEmpty = !value
    const displayValue = isEmpty
      ? `No ${label.toLowerCase()} provided.`
      : value

    return (
      <div
        className="bug-report__editable-text"
        onClick={() => startEdit(field)}
        onKeyDown={(e) => handleEditableKeyDown(e, field)}
        role="button"
        tabIndex={0}
        aria-label={`Edit ${label.toLowerCase()}`}
        data-testid={`bug-${field.replace(/_/g, '-')}`}
        data-analytics-event={`bug-${field.replace(/_/g, '-')}-edit`}
      >
        {field === 'reproduction_steps' ? (
          <pre className="bug-report__steps">{displayValue}</pre>
        ) : (
          <p className={isEmpty ? 'bug-report__empty-text' : ''}>{displayValue}</p>
        )}
      </div>
    )
  }

  // Collapsible "Full Bug Report" section at the end of the report: the complete markdown users paste
  // into a coding agent. Collapsed by default so the rendered analysis stays the primary reading
  // surface; the Copy / Download actions live in the header toolbar next to the title.
  function renderMarkdownSection() {
    return (
      <section className="bug-report__section bug-report__report-banner" data-testid="bug-report-banner">
        <div className="bug-report__report-banner-bar">
          <button
            type="button"
            className="bug-report__report-banner-toggle"
            onClick={() => setMarkdownExpanded(e => !e)}
            aria-expanded={markdownExpanded}
            data-testid="bug-report-markdown-toggle"
            data-analytics-event="bug-report-markdown-toggle"
          >
            {markdownExpanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
            <span>Full Bug Report</span>
          </button>
          <span className="bug-report__report-banner-hint">Markdown for Claude Code, Cursor, and other coding agents</span>
        </div>
        {markdownExpanded && (
          <div className="bug-report__report-banner-panel" data-testid="bug-report-markdown">
            <p className="bug-report__markdown-help">
              Copy this report and paste it into your coding agent (Claude Code, Cursor, etc.) to have it fix the issue.
            </p>
            <pre className="bug-report__markdown-preview" data-testid="bug-report-markdown-content">{buildMarkdown(bug)}</pre>
          </div>
        )}
      </section>
    )
  }

  function renderImpact() {
    const impact = securityImpact(displayBug.analysis)
    if (displayBug.polish_category !== 'security' || !impact) return null
    return <section className="bug-report__section" data-testid="bug-security-impact">
      <h2>Impact</h2>
      <p>{impact}</p>
    </section>
  }

  function renderAnalysisContent() {
    const analysis = displayBug.analysis!

    return (
      <div className="bug-report__content">
        {resolvingEvidence && (
          <div className="bug-report__evidence-banner" data-testid="resolving-evidence">
            <Spinner size={14} className="bug-report__spinner" />
            Resolving evidence from recording...
          </div>
        )}

        {/* Actual and expected side by side on wide screens — the two columns read as a direct
            comparison; they stack again below 640px (see .bug-report__behavior-grid). */}
        <div className="bug-report__section bug-report__behavior-grid">
          <section className="bug-report__behavior bug-report__section--actual" data-testid="analysis-actual-behavior">
            <h2><XCircle size={15} aria-hidden />Actual Behavior</h2>
            {renderEditableField('actual_behavior', 'Actual behavior', true)}
          </section>

          <section className="bug-report__behavior bug-report__section--expected">
            <h2><CheckCircle2 size={15} aria-hidden />Expected Behavior</h2>
            {renderEditableField('expected_behavior', 'Expected behavior', true)}
          </section>
        </div>

        {renderImpact()}

        {analysis.root_cause && (
          <AnalysisSectionView
            section={analysis.root_cause}
            label="Root Cause"
            recordingId={bug.replay_recording_id}
          />
        )}

        {analysis.chronology && analysis.chronology.length > 0 && (
          <ChronologySection steps={analysis.chronology} recordingId={bug.replay_recording_id} />
        )}

        {analysis.chain && analysis.chain.length > 0 && (
          <section className="bug-report__section" data-testid="analysis-causal-chain">
            <h2>Root Cause to Actual Behavior</h2>
            <ol className="bug-report__chain-list">
              {analysis.chain.map((step, idx) => (
                <li key={idx} className="bug-report__chain-step">
                  <p>{step.text ?? ''}</p>
                  {step.evidence && step.evidence.length > 0 && (
                    <EvidencePanel items={step.evidence} recordingId={bug.replay_recording_id} />
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    )
  }

  function renderLegacyContent() {
    return (
      <div className="bug-report__content">
        <section className="bug-report__section">
          <h2>Description</h2>
          {renderEditableField('description', 'Description', true)}
        </section>

        <section className="bug-report__section">
          <h2>Steps to Reproduce</h2>
          {renderEditableField('reproduction_steps', 'Reproduction steps', true)}
        </section>

        <section className="bug-report__section bug-report__section--expected">
          <h2><CheckCircle2 size={15} aria-hidden />Expected Behavior</h2>
          {renderEditableField('expected_behavior', 'Expected behavior', true)}
        </section>
        {renderImpact()}
      </div>
    )
  }

  return (
    <div className="bug-report" data-testid="bug-report">
      <header className="bug-report__header">
        {!showStatusSelector && (
          <div className="bug-report__sticky-toolbar">
            <button type="button" className="bug-report__delete-btn" onClick={onDeleteBug} data-testid="delete-bug-btn" data-analytics-event="bug-delete-button">
              <Trash2 size={16} aria-hidden="true" />
              <span className="bug-report__delete-label">Delete Bug</span>
            </button>
          </div>
        )}
        <div className="bug-report__title-row">
          {/* Top line: icon-only back arrow (matching the Bugs / Test Runs headers) followed by the
              status, severity, type, environment and journey chips. The title gets its own line. */}
          <div className="bug-report__meta-line">
            <button
              type="button"
              className="bug-report__back"
              onClick={onNavigateBack}
              aria-label={backLabel}
              title={backLabel}
              data-testid="back-btn"
              data-analytics-event="bug-back"
            >
              <ArrowLeft size={18} aria-hidden />
            </button>
        <div className="bug-report__badges">
            {/* Status chip doubles as the quick status menu; the rail carries the labelled control. */}
            <div ref={statusRef} className="bug-report__severity-wrapper" data-testid="status-chip-dropdown">
              <button
                type="button"
                ref={statusBtnRef}
                onClick={() => setShowStatusDropdown(s => !s)}
                className="bug-report__badge-btn"
                title={STATUS_TOOLTIPS[displayBug.status] ?? displayBug.status}
                aria-label={`Status: ${STATUS_LABELS[displayBug.status] ?? displayBug.status}`}
                aria-haspopup="true"
                aria-expanded={showStatusDropdown}
                data-testid="bug-header-status"
                data-analytics-event="bug-status-chip-toggle"
              >
                <StatusBadge status={displayBug.status} variant="bug" />
                <ChevronDown size={12} className="bug-report__badge-caret" aria-hidden="true" />
              </button>
              {showStatusDropdown && (
                <div className="bug-report__dropdown" role="menu" aria-label="Status options" onKeyDown={handleStatusDropdownKeyDown}>
                  {USER_SETTABLE_STATUSES.map(s => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      className="bug-report__dropdown-item"
                      role="menuitem"
                      tabIndex={-1}
                      data-testid={`set-status-chip-${s}`}
                      data-analytics-event={`bug-status-chip-${s}`}
                    >
                      <StatusBadge status={s} variant="bug" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div ref={severityRef} className="bug-report__severity-wrapper" data-testid="severity-dropdown">
              <button
                type="button"
                ref={severityBtnRef}
                onClick={() => setShowSeverityDropdown(s => !s)}
                className="bug-report__badge-btn"
                title={SEVERITY_TOOLTIPS[displayBug.severity] ?? displayBug.severity}
                aria-label={`Severity: ${SEVERITY_TOOLTIPS[displayBug.severity] ?? displayBug.severity}`}
                aria-haspopup="true"
                aria-expanded={showSeverityDropdown}
                data-testid="severity-badge-btn"
                data-analytics-event="bug-severity-toggle"
              >
                <StatusBadge status={displayBug.severity} variant="severity" />
                <ChevronDown size={12} className="bug-report__badge-caret" aria-hidden="true" />
              </button>
              {showSeverityDropdown && (
                <div className="bug-report__dropdown" role="menu" aria-label="Severity options" onKeyDown={handleSeverityDropdownKeyDown}>
                  {SEVERITIES.map(s => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => handleSeverityChange(s)}
                      className="bug-report__dropdown-item"
                      role="menuitem"
                      tabIndex={-1}
                      data-testid={`set-severity-${s}`}
                      data-analytics-event={`bug-severity-${s}`}
                    >
                      <StatusBadge status={s} variant="severity" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {category && (
              <span className="bug-report__category-badge" data-testid="bug-category" title="Bug type is set by the pipeline">
                {bugCategoryLabel(category)}
              </span>
            )}
            <BugEnvironment bug={displayBug} />
            {displayBug.journey_id && <Link
              className="bug-report__journey-link"
              to={`/projects/${displayBug.project_id}/journeys/${displayBug.journey_id}`}
              title="Open the journey that generated this bug"
              data-testid="bug-report-journey-link"
              data-analytics-event="bug-journey-link"
            ><MousePointerClick size={14} aria-hidden />Journey: {displayBug.journey_name || displayBug.journey_id}</Link>}
            </div>
          </div>
          {editingField === 'title' ? (
            <div className="bug-report__title-edit">
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                className="bug-report__title-input"
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => handleBlur('title')}
                onKeyDown={(e) => handleKeyDownOnInput(e, 'title')}
                aria-label="Bug title"
                aria-describedby={validationError ? 'title-error' : undefined}
                data-testid="edit-title"
                data-analytics-event="bug-title-input"
              />
              {validationError && (
                <span className="bug-report__validation-error" id="title-error" role="alert" data-testid="validation-error">{validationError}</span>
              )}
            </div>
          ) : (
            <h1
              className="bug-report__title"
              onClick={() => startEdit('title')}
              onKeyDown={(e) => handleEditableKeyDown(e, 'title')}
              tabIndex={0}
              role="button"
              aria-label={`Edit title: ${displayBug.title}`}
              data-testid="bug-title"
              data-analytics-event="bug-title-edit"
            >
              {displayBug.title}
            </h1>
          )}
          <BugPullRequests bug={displayBug} />
        </div>
      </header>

      {/* Evidence first, directly under the title: what the report says next is a description of
          what this replay shows. */}
      <BugReplayCard bug={displayBug} />

      {mobileSidebar && (
        <div className="bug-report__mobile-sidebar">
          {mobileSidebar}
        </div>
      )}

      <div className="bug-report__scroll-body">
        {adminMode && <BugJudgeLinks bug={displayBug} />}

        {bug.status === 'invalid' && <DiscardedReportNotice bug={bug} />}

        {hasAnalysis ? renderAnalysisContent() : renderLegacyContent()}

        {renderMarkdownSection()}
      </div>
    </div>
  )
}
